import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { setIsDrawingField, addDrawnField } from 'js/reducers/AddFieldReducer';
import {
  setIsDrawingNote,
  createDefaultNote,
  selectNote,
  setIsMagicSelecting,
  setMagicWandThreshold,
  setIsPlacingMarker,
} from 'js/reducers/NoteReducer';
import PolygonDrawing from 'js/components/PolygonDrawing/PolygonDrawing';
import { getSelectedFieldImageForDate } from 'js/helpers/StateInterpreters';
import { useLangFile } from 'js/context/LanguageContext';
import { useGoogleMap } from 'js/context/GoogleMapContext';
import { useSelectedSeason } from '../../context/SeasonContext';
import {
  addFieldHole,
  saveEditedFieldBoundaries,
  setIsDrawingFieldHole,
  setIsEditingFieldBoundaries,
  setIsSplittingField,
  setIsEditingMergedField,
  splitField,
  mergeFields,
  selectField,
} from '../../reducers/FieldReducer';
import { setViewMode } from '../../reducers/ControlReducer';
import ViewModeConstants from '../../constants/ViewModeConstants';
import { hideProgress, showProgress } from '../../reducers/ProgressReducer';
import { PolygonDrawingProvider } from './PolygonDrawingContext';
import { Action, useActionSnackbarContext } from '../ActionSnackbarHandler/ActionSnackbarHandler';
import { fitMapToBounds, mergeBounds } from '../../helpers/MapsUtils';
import Bounds from 'js/model/Bounds';
import useFirebaseAnalytics, { FIREBASE_EVENTS } from '../../hooks/useFirebaseAnalytics';
import { clearFieldYieldData } from '../../reducers/YieldReducer';
import { clearFieldSurveyData } from '../../reducers/SurveyReducer';
import { getLat, getLng } from './PolygonDrawingUtils';

const getCurrentHectares = (fields) => {
  return [...fields.values()].reduce((acc, f) => acc + (f.size || 0), 0).toFixed(1);
};

const mapStateToProps = (state) => {
  return {
    fields: state.field.fields,
    isDrawingField: state.addField.isDrawing,
    isDrawingNote: state.note.isDrawing,
    isPlacingMarker: state.note.isPlacingMarker,
    farm: state.farm.farm,
    magicWand: state.note.magicWand,
    isMagicSelecting: state.note.magicWand.isSelecting,
    imageType: state.field.imageType,
    date: state.field.date?.date,
    images: state.field.images,
    selectedField: state.field.selectedField,
    surveys: state.survey.surveys,
    selectedLayer: state.survey.selectedLayer,
    showSoilSurveys: state.survey.showSoilSurveys,
    isDrawingFieldHole: state.field.isDrawingFieldHole,
    isEditingFieldBoundaries: state.field.isEditingFieldBoundaries,
    isSplittingField: state.field.isSplittingField,
    isEditingMergedField: state.field.isEditingMergedField,
    mergedFields: state.field.mergedFields,
  };
};

const PolygonDrawingContainer = (props) => {
  const LangFile = useLangFile();
  const googleMap = useGoogleMap();
  const selectedSeason = useSelectedSeason();
  const { addAction } = useActionSnackbarContext();
  const analytics = useFirebaseAnalytics();

  const {
    dispatch,
    farm,
    isDrawingField,
    isMagicSelecting,
    isDrawingNote,
    isDrawingFieldHole,
    isEditingFieldBoundaries,
    isSplittingField,
    isEditingMergedField,
    fields,
    isPlacingMarker,
  } = props;

  const [currentHectare, setCurrentHectare] = useState(0);

  const handleSetIsDrawingNote = (value) => {
    dispatch(setIsDrawingNote(value));
  };

  const handleSetIsMagicSelecting = (value) => {
    dispatch(setIsMagicSelecting(value));
  };

  const handleSetIsPlacingMarker = (value) => {
    dispatch(setIsPlacingMarker(value));
  };

  const handleSetIsDrawingField = (value) => {
    dispatch(setIsDrawingField(value));
  };

  const handleAddDrawnField = (drawnPolygon) => {
    // let totalSizeToAdd = areaCalculation({coords: drawnPolygon}).size;

    dispatch(addDrawnField(drawnPolygon, farm.farmId, selectedSeason.id));
  };

  const handleSetIsDrawingFieldHole = (value) => {
    dispatch(setIsDrawingFieldHole(value));
  };

  const handleAddDrawnFieldHole = (value) => {
    dispatch(showProgress());
    dispatch(
      addFieldHole(
        props.selectedField.fieldId,
        selectedSeason.id,
        props.selectedField.polygon.coordinates,
        value,
        props.selectedField.size
      )
    )
      .then(() => {
        analytics.logEvent(FIREBASE_EVENTS.EF_FIELD_HOLE_ADDED);

        dispatch(clearFieldYieldData(props.selectedField.fieldId));
        dispatch(clearFieldSurveyData(props.selectedField.fieldId));
        handleReturnToOverview(props.selectedField.bounds);
      })
      .catch((e) => {
        handleError(e);
      });
  };

  const handleSetIsEditingFieldBoundaries = (value) => {
    dispatch(setIsEditingFieldBoundaries(value));
  };

  const handleSaveEditedFieldBoundaries = (editedFieldBoundaries, fieldHolesToKeep, size) => {
    editedFieldBoundaries = [editedFieldBoundaries];
    dispatch(showProgress());
    dispatch(
      saveEditedFieldBoundaries(
        props.selectedField.fieldId,
        selectedSeason.id,
        editedFieldBoundaries,
        fieldHolesToKeep,
        size
      )
    )
      .then((action) => {
        analytics.logEvent(FIREBASE_EVENTS.EF_FIELD_BOUNDARIES_UPDATED);

        dispatch(clearFieldYieldData(props.selectedField.fieldId));
        dispatch(clearFieldSurveyData(props.selectedField.fieldId));
        const bounds = Bounds.fromString(action.action.payload.bounds);
        handleReturnToOverview(bounds);
      })
      .catch((e) => {
        handleError(e);
      });
  };

  const handleSetIsSplittingField = (value) => {
    dispatch(setIsSplittingField(value));
  };

  // WAIT till backend supports this change
  const handleSplitField = (newFieldA, newFieldB) => {
    const updatedFieldPolygonCoords = [
      newFieldA.coordinates.map((coordinate) => {
        return [coordinate[0], coordinate[1]];
      }),
    ];
    if (newFieldA.fieldHolesToKeep.length > 0) {
      updatedFieldPolygonCoords.push(...newFieldA.fieldHolesToKeep);
    }

    const newFieldAObject = {
      coordinates: updatedFieldPolygonCoords,
      name: newFieldA.name,
      mef: newFieldA.mef,
    };

    const newFieldPolygonCoords = [
      newFieldB.coordinates.map((coordinate) => {
        return [coordinate[0], coordinate[1]];
      }),
    ];
    if (newFieldB.fieldHolesToKeep.length > 0) {
      newFieldPolygonCoords.push(...newFieldB.fieldHolesToKeep);
    }
    const newFieldBObject = {
      coordinates: newFieldPolygonCoords,
      name: newFieldB.name,
      mef: newFieldB.mef,
      season: selectedSeason.id,
    };

    dispatch(showProgress());
    dispatch(
      splitField(
        props.selectedField.fieldId,
        props.selectedField.polygonWkt,
        selectedSeason.id,
        newFieldAObject,
        newFieldBObject
      )
    )
      .then((action) => {
        analytics.logEvent(FIREBASE_EVENTS.EF_FIELD_SPLIT);

        const childFieldABounds = Bounds.fromString(action.action.payload.childFields[0].bounds);
        const childFieldBBounds = Bounds.fromString(action.action.payload.childFields[1].bounds);
        const bounds = mergeBounds(childFieldABounds, childFieldBBounds);

        handleReturnToOverview(bounds);
      })
      .catch((e) => {
        handleError(e);
      });
  };

  const handleSetIsEditingMergedField = (value) => {
    dispatch(setIsEditingMergedField(value));
  };

  const handleMergeFields = (
    parentFieldIds,
    newCoordinates,
    fieldHolesToKeep,
    newName,
    newFieldNumber
  ) => {
    const updatedFieldPolygonCoords = [
      newCoordinates.map((coordinate) => {
        return [coordinate[0], coordinate[1]];
      }),
    ];
    if (fieldHolesToKeep.length > 0) {
      updatedFieldPolygonCoords.push(...fieldHolesToKeep);
    }
    dispatch(showProgress());
    dispatch(
      mergeFields(
        parentFieldIds,
        selectedSeason.id,
        updatedFieldPolygonCoords,
        newName,
        newFieldNumber
      )
    )
      .then((action) => {
        analytics.logEvent(FIREBASE_EVENTS.EF_FIELD_MERGED);

        const bounds = Bounds.fromString(action.action.payload.bounds);
        handleReturnToOverview(bounds);
      })
      .catch((e) => {
        handleError(e);
      });
  };

  const handleReturnToOverview = (bounds) => {
    dispatch(hideProgress());
    dispatch(setViewMode(ViewModeConstants.OVERVIEW));
    if (bounds) {
      fitMapToBounds(googleMap, bounds);
    }
    dispatch(selectField(null));
  };

  const handleReturnToEditFieldView = () => {
    dispatch(setViewMode(ViewModeConstants.EDIT_FIELD));
  };

  const handleOnMarkerPlaced = useCallback((marker) => {
    const nodes = [];
    const node = {};
    node.latitude = getLat(marker.position);
    node.longitude = getLng(marker.position);
    nodes.push(node);
    marker.setMap(null);

    const note = createDefaultNote(nodes);

    dispatch(selectNote(note));
  }, []);

  const handleNoToolSelected = () => {
    addAction(
      new Action(
        'no-tool-selected',
        LangFile.PolygonDrawing.noToolSelectedMessage,
        'warning',
        'filled'
      )
    );
  };

  const handleError = (error) => {
    addAction(
      new Action(
        'merge-error',
        `${LangFile.ErrorSnackbar.errorMessage}: ${error.response.data}`,
        'error',
        'filled'
      )
    );
    handleReturnToOverview();
  };

  const childProps = {
    ...props,
    map: googleMap,
    LangFile: LangFile,
    currentHectare: currentHectare,
    setIsDrawingField: handleSetIsDrawingField,
    setIsDrawingNote: handleSetIsDrawingNote,
    setIsDrawingFieldHole: handleSetIsDrawingFieldHole,
    setIsEditingFieldBoundaries: handleSetIsEditingFieldBoundaries,
    setIsSplittingField: handleSetIsSplittingField,
    setIsEditingMergedField: handleSetIsEditingMergedField,
    setIsMagicSelecting: handleSetIsMagicSelecting,
    setIsPlacingMarker: handleSetIsPlacingMarker,
    onMarkerPlaced: handleOnMarkerPlaced,
    addDrawnField: handleAddDrawnField,
    addDrawnFieldHole: handleAddDrawnFieldHole,
    saveEditedFieldBoundaries: handleSaveEditedFieldBoundaries,
    splitField: handleSplitField,
    mergeFields: handleMergeFields,
    selectAndCreateNote: (outline) => dispatch(selectNote(createDefaultNote(outline))),
    onSetMagicWandThreshold: (threshold) => dispatch(setMagicWandThreshold(threshold)),
    returnToEditFieldView: () => handleReturnToEditFieldView(),
    onNoToolSelected: handleNoToolSelected,
    fieldImage: getSelectedFieldImageForDate(
      props.selectedField,
      props.date,
      props.images,
      props.imageType
    ),
  };

  useEffect(() => {
    if (fields.size > 0) {
      setCurrentHectare(getCurrentHectares(fields));
    }
  }, [fields]);

  if (
    isDrawingNote ||
    isDrawingField ||
    isMagicSelecting ||
    isPlacingMarker ||
    isDrawingFieldHole ||
    isEditingFieldBoundaries ||
    isSplittingField ||
    isEditingMergedField
  ) {
    return (
      <PolygonDrawingProvider>
        <PolygonDrawing {...childProps} />
      </PolygonDrawingProvider>
    );
  } else {
    return null;
  }
};

export default connect(mapStateToProps)(PolygonDrawingContainer);
